import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

export type AssessmentErrorKind = "load" | "save";

type AssessmentErrorModalProps = {
  open: boolean;
  kind: AssessmentErrorKind;
  isRetrying?: boolean;
  showSaveAndQuit?: boolean;
  onRetry: () => void;
  onSaveAndQuit: () => void;
};

const descriptionByKind: Record<AssessmentErrorKind, string> = {
  load: "Something went wrong on our end, not yours. Select Try Again to reload this part.",
  save: "Something went wrong on our end, not yours. Select Try Again so we can save your answers.",
};

export function AssessmentErrorModal({
  open,
  kind,
  isRetrying = false,
  showSaveAndQuit = false,
  onRetry,
  onSaveAndQuit,
}: AssessmentErrorModalProps) {
  return (
    <Dialog
      open={open}
      maxWidth={"sm"}
      fullWidth
      role="alertdialog"
      aria-labelledby="assessment-error-title"
      aria-describedby="assessment-error-description"
      data-testid="assessment-error-modal"
      slotProps={{ paper: { sx: { p: 2 } } }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
        <WarningRoundedIcon color="primary" sx={{ fontSize: 48 }} />
      </Box>
      <DialogTitle
        id="assessment-error-title"
        sx={{ textAlign: "center", color: "text.primary" }}
      >
        Let&apos;s try that again.
      </DialogTitle>
      <DialogContent>
        <Typography
          id="assessment-error-description"
          color="text.secondary"
          sx={{ fontWeight: 600 }}
        >
          {descriptionByKind[kind]}
        </Typography>
        {showSaveAndQuit ? (
          <Typography color="text.secondary" sx={{ fontWeight: 600, mt: 2 }}>
            If this keeps happening, select Save and Quit and come back later.
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1.5 }}>
        {showSaveAndQuit ? (
          <Button
            variant="outlined"
            disabled={isRetrying}
            onClick={onSaveAndQuit}
          >
            Save and Quit
          </Button>
        ) : null}
        <Button
          variant="contained"
          disabled={isRetrying}
          onClick={onRetry}
          startIcon={
            isRetrying ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          Try Again
        </Button>
      </DialogActions>
    </Dialog>
  );
}
